/* ============================================================
 * <trust-badges-marquee>
 *
 * JS-driven seamless marquee for the trust-badges section.
 * Pattern adapted from Shopify Horizon's marquee-component:
 *
 *   1. Measure viewport vs. one content "set".
 *   2. Clone items inside the set until it spans >= one viewport.
 *      (Fixes the empty-start case when content is narrower than
 *      the viewport.)
 *   3. Clone the whole set once for the seamless 2-set loop —
 *      CSS keyframe translates -50% - gap/2 to land on the seam.
 *   4. Scale duration by sqrt(copies) so perceived speed stays
 *      roughly consistent regardless of how much content was
 *      cloned to fill the viewport.
 *   5. Optional smooth hover slowdown via the Web Animations
 *      API's playbackRate (animations[0].playbackRate) instead
 *      of a hard CSS animation-play-state pause.
 * ============================================================ */

(function () {
  if (customElements.get('trust-badges-marquee')) return;

  class TrustBadgesMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready !== undefined) return;

      this.track = this.querySelector('[data-marquee-track]');
      this.set = this.querySelector('[data-marquee-set]');
      if (!this.track || !this.set) return;

      // Snapshot the originals so resize rebuilds from a clean state
      // instead of compounding clones every iteration.
      this.originalItems = Array.from(this.set.children).map((n) => n.cloneNode(true));
      this.speedFactor = Number(this.dataset.speedFactor) || 25;
      this.rateRaf = null;

      // Bind once so add/remove pair the same function reference.
      this.handleResize = this.debounce(() => this.build(), 200);
      this.handlePointerEnter = () => this.easePlaybackRate(0, 300);
      this.handlePointerLeave = () => this.easePlaybackRate(1, 300);

      this.build();

      // Re-measure once fonts are ready — Cairo loads async, and an
      // initial measurement in the fallback font produces items that
      // are the wrong width once Cairo arrives.
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.build());
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      }

      if (this.hasAttribute('data-pause-on-hover')) {
        this.addEventListener('pointerenter', this.handlePointerEnter);
        this.addEventListener('pointerleave', this.handlePointerLeave);
        this.addEventListener('focusin', this.handlePointerEnter);
        this.addEventListener('focusout', this.handlePointerLeave);
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeEventListener('focusin', this.handlePointerEnter);
      this.removeEventListener('focusout', this.handlePointerLeave);
      if (this.rateRaf) cancelAnimationFrame(this.rateRaf);
    }

    build() {
      // Reset to originals, then strip any prior whole-set clone.
      this.set.replaceChildren(...this.originalItems.map((n) => n.cloneNode(true)));
      this.track.querySelectorAll('[data-marquee-clone-set]').forEach((n) => n.remove());
      this.removeAttribute('data-ready');

      // Force layout once before measuring.
      const viewportWidth = this.offsetWidth;
      const setWidth = this.set.scrollWidth;
      if (!viewportWidth || !setWidth) return;

      // Fill the set with item-clones until it's at least one viewport
      // wide. This is what guarantees no empty space, no matter how
      // narrow the original content is.
      let copies = 1;
      if (setWidth < viewportWidth) {
        copies = Math.ceil(viewportWidth / setWidth);
        const baseChildren = Array.from(this.set.children);
        for (let i = 1; i < copies; i++) {
          for (const child of baseChildren) {
            const clone = child.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            this.set.appendChild(clone);
          }
        }
      }

      // Clone the whole set once so the track is exactly 2 sets wide.
      const cloneSet = this.set.cloneNode(true);
      cloneSet.setAttribute('aria-hidden', 'true');
      cloneSet.setAttribute('data-marquee-clone-set', '');
      cloneSet.removeAttribute('data-marquee-set');
      this.track.appendChild(cloneSet);

      // Duration scales by sqrt(copies) — Horizon's trick to keep the
      // visible pixel-per-second speed feel ~constant across content
      // sizes. Doubling copies only multiplies duration by ~1.4×.
      const speed = Math.sqrt(copies) * this.speedFactor;
      this.style.setProperty('--marquee-speed', speed + 's');

      // CSS animation is gated on this flag — see trust-badges.css.
      this.setAttribute('data-ready', '');
    }

    easePlaybackRate(target, duration) {
      const anim = this.track.getAnimations?.()[0];
      if (!anim) return;

      if (this.rateRaf) cancelAnimationFrame(this.rateRaf);
      const start = anim.playbackRate;
      const startTime = performance.now();

      const tick = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        const eased = t * t * (3 - 2 * t); // smoothstep
        anim.playbackRate = start + (target - start) * eased;
        if (t < 1) {
          this.rateRaf = requestAnimationFrame(tick);
        } else {
          this.rateRaf = null;
        }
      };
      this.rateRaf = requestAnimationFrame(tick);
    }

    debounce(fn, wait) {
      let timer;
      return () => {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }
  }

  customElements.define('trust-badges-marquee', TrustBadgesMarquee);
})();
